import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import { API_BASE } from '../config/api.config.js';
import { SUPPORT_CONFIG, getMailtoLink } from '../config/support.config';

/**
 * Email Verification Page Component
 * T-011a: Handles email verification from welcome email links
 *
 * URL format: /verify-email?token=xxx
 */
export default function VerifyEmail({ token, onComplete }) {
  const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'error' | 'expired'
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('No verification token provided.');
      return;
    }

    verifyEmail(token);
  }, [token]);

  const verifyEmail = async (verificationToken) => {
    try {
      const response = await fetch(`${API_BASE}/auth/email/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ token: verificationToken }),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setMessage(data.message || 'Your email has been verified successfully!');
      } else {
        // Check for specific error types
        if (data.detail?.includes('expired')) {
          setStatus('expired');
          setMessage('This verification link has expired. Please request a new one.');
        } else if (data.detail?.includes('already verified')) {
          setStatus('success');
          setMessage('Your email is already verified. You can continue shopping!');
        } else {
          setStatus('error');
          setMessage(data.detail || 'Failed to verify email. Please try again.');
        }
      }
    } catch (error) {
      console.error('Email verification error:', error);
      setStatus('error');
      setMessage('Network error. Please check your connection and try again.');
    }
  };

  const handleContinue = () => {
    // Clear the URL parameters and go to homepage
    window.history.replaceState({}, '', '/');
    if (onComplete) {
      onComplete();
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-zinc-800 border border-orange-500/30 rounded-2xl flex items-center justify-center shadow-lg shadow-orange-500/10 mx-auto mb-4">
            <span className="font-comic text-3xl text-orange-500">M</span>
          </div>
          <h1 className="font-maestro-mono text-2xl font-bold" style={{ letterSpacing: '0.15em' }}><span className="text-maestro-gold">M</span><span className="text-white">AESTRO</span></h1>
        </div>

        {/* Verification Card */}
        <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-8 text-center">
          {/* Verifying State */}
          {status === 'verifying' && (
            <>
              <div className="w-16 h-16 bg-orange-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Verifying Your Email</h2>
              <p className="text-zinc-400">Please wait while we verify your email address...</p>
            </>
          )}

          {/* Success State */}
          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Email Verified!</h2>
              <p className="text-zinc-400 mb-6">{message}</p>
              <button
                onClick={handleContinue}
                className="w-full py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 transition-colors"
              >
                Continue Shopping
              </button>
            </>
          )}

          {/* Error State */}
          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Verification Failed</h2>
              <p className="text-zinc-400 mb-6">{message}</p>
              <button
                onClick={handleContinue}
                className="w-full py-3 bg-zinc-800 text-white rounded-xl font-semibold hover:bg-zinc-700 transition-colors"
              >
                Go to Homepage
              </button>
            </>
          )}

          {/* Expired State */}
          {status === 'expired' && (
            <>
              <div className="w-16 h-16 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-yellow-500" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Link Expired</h2>
              <p className="text-zinc-400 mb-6">{message}</p>
              <div className="space-y-3">
                <button
                  onClick={handleContinue}
                  className="w-full py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 transition-colors"
                >
                  Sign In to Resend
                </button>
                <button
                  onClick={handleContinue}
                  className="w-full py-3 bg-zinc-800 text-white rounded-xl font-semibold hover:bg-zinc-700 transition-colors"
                >
                  Go to Homepage
                </button>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-zinc-600 text-sm mt-6">
          Need help? Contact{' '}
          <a href={getMailtoLink('general', 'Email Verification Help')} className="text-orange-500 hover:text-orange-400">
            {SUPPORT_CONFIG.emails.general}
          </a>
        </p>
      </div>
    </div>
  );
}
